import React, { useEffect, useState } from 'react'
import CircularProgress from '@mui/material/CircularProgress';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import ExportData from 'highcharts/modules/export-data';
import Accessibility from 'highcharts/modules/accessibility';
import HighchartsReact from 'highcharts-react-official';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, isValid } from 'date-fns'

Exporting(Highcharts)
ExportData(Highcharts)
Accessibility(Highcharts)

const AccumPcpnGrf = (props) => { 
    const { smallScreen } = props;
    const [ selectedStartDate, setSelectedStartDate ] = useState();
    const [ maxDate, setMaxDate ] = useState();
    const [ minDate, setMinDate ] = useState();

    // stations for graph
    const sids = ["USW00054768","USC00190120","USC00197627","USC00194744","USC00195984","USC00193821","USC00192501"];

    // options for Highcharts plot
    const default_options = {
      chart: {
        zoomType: "x",
        height: Math.min((window.innerHeight * 0.75), 780),
        width: window.innerWidth - (smallScreen ? 0 : 200) - 32,
      },
      colors: [
				'#7cb5ec',
				'#434348',
				'#90ed7d',
				'#f7a35c',
				'#8085e9',
				'#f15c80',
				'#e4d354',
				'#2b908f',
				'#f45b5b',
				'#91e8e1'
			],
      plotOptions: {
        line: {
            cursor: "pointer",
            lineWidth: 2,
            states: {
                hover: {
                    enabled: false
                }
            },
            marker: {
                enabled: false,
                symbol: "diamond",
                fillColor: "black",
                radius: 3
            }
        },
        series: {
          pointPlacement:"on"
        }
      },
      xAxis: {
        type: "datetime",
        dateTimeLabelFormats: {
            day: "%b %e",
            week: "%b %e",
            month: "%b",
            year: "%b"
        },
        gridLineWidth: 1,
      },
      yAxis: {
        title: {
            text: "Accumulated Departure (inches)"
        },
        plotLines: [{
            width: 2,
            value: 0
        }],
      },
      legend: {
        alignColumns: false,
      },
      exporting: {
        menuItemDefinitions: {
          // Custom definition
          downloadCSV: {
            text: 'Download data as CSV'
          },
        },
        buttons: {
          contextButton: {
            menuItems: [
               "downloadPNG",
               "downloadCSV",
             ]
          }
        }
      },
      credits: {
        href: "https://www.rcc-acis.org",
        text: "Powered by ACIS"
      },
      tooltip: {
        shared: true,
        dateTimeLabelFormats: {
            month: "%A, %b %e, %Y",
            day: "%A, %b %e, %Y",
            hour: "%A, %b %e, %Y",
            minute: "%A, %b %e, %Y",
            second: "%A, %b %e, %Y",
            millisecond: "%A, %b %e, %Y",
        },
      },
      title: {
        text: "Accumulated Precipitation Departure from Normal (inches)",
      },
      subtitle: {
        text: null,    //added in fill_graph
      },
      series: null,    //added in fill_graph
    };

    const [ options, setOptions ] = useState(default_options);

    function fill_graph(smry) {
      var resVal, dateVal, data_series,
          needSubtitle = false,
          all_data_series = [];
      // for each station
      smry.forEach(stndata => {
        dateVal = new Date(+selectedStartDate);
        dateVal.setUTCHours(0)
        data_series = [];
        // for each day
        stndata.data.forEach((res, i) => {
          if (i > 0) {
            dateVal.setDate(dateVal.getDate() + 1)
          }
          if (res[0] !== "M") {
            if (res[0] === "T") {
              resVal = 0.0;
            } else {
              resVal = parseFloat(res[0]);
            }
            if (res[1] === "M" || res[1] === "S") {
              data_series.push({x: dateVal.getTime(), y: resVal, marker: {enabled: true}});
              needSubtitle = true;
            } else {
              data_series.push([dateVal.getTime(), resVal]);
            }
          }
        });
        if (data_series.length > 0) {
          all_data_series.push({
            name: stndata.meta.name,
            data: data_series,
            type: "line",
            tooltip: {
              valueDecimals: 2,
              valueSuffix: " inches"
            }
          });
        }
      });
      // trigger drawing of graph
		  setOptions({...options, series: all_data_series, subtitle: {text: needSubtitle ? "Black diamonds represent missing values" : ""}});
	  };

    const fetchPcpn = (params) => {
        const jsonParams = JSON.stringify({params});
        fetch("https://data.rcc-acis.org/MultiStnData", {body: jsonParams, method: "POST", headers: {'Content-Type': 'application/json'}})
            .then(response => {
                if (!response.ok) {
                    console.log("Error fetching the results");
                    return;
                }
                return response.json();
            })
            .then(response => {
                fill_graph(response.data);
            })
            .catch(err => {
                console.log(err.message);
            });
    };

    useEffect(() => {
      const maxDate = new Date();
      const minDate = new Date();
      minDate.setFullYear(minDate.getFullYear()-1);
      minDate.setDate(minDate.getDate()+1);
      setMinDate(minDate);
      setMaxDate(maxDate);
      setSelectedStartDate(minDate);
    },[] )

    useEffect(() => {
      if (isValid(selectedStartDate) && selectedStartDate >= minDate && selectedStartDate <= maxDate) {
        const params = {
          "sids": sids,
          "sDate": format(selectedStartDate, 'yyyy-MM-dd'),
          "eDate": format(maxDate, 'yyyy-MM-dd'),
          "meta": ["name","state","sids"],
          "elems": [{"name":"pcpn","interval":[0,0,1],"duration":"std","season_start":format(selectedStartDate, 'MM-dd'),"reduce":"sum","normal":"departure"},
                {"name":"pcpn","interval":[0,0,1],"duration":1}]
        };
        setOptions({...options, series: null});
        fetchPcpn(params);
      } else {
        setOptions({...options, series: {}})
      }
      // eslint-disable-next-line
    }, [selectedStartDate]);

    return (
      <>
          {options.series ?
            <>
              <HighchartsReact
                  highcharts={Highcharts}
                  options={options}
              />
              <DatePicker 
                value={selectedStartDate}
                onChange={setSelectedStartDate}
                format='yyyy-MM-dd'
                label="Accumulation start date"
                minDate={minDate}
                maxDate={maxDate}
              />
            </>
          :
            <>
              <CircularProgress />
              <span>Loading ...</span>
            </>
          }
      </>
  )
}

export default AccumPcpnGrf;
